/**
 * Client-side conversion of user-selected files into compressed, 72-DPI PDFs.
 *
 * Upload policy (USER UPLOADS ONLY — does not apply to the app-generated
 * PDF pipeline in `lib/r2.ts` / `lib/pdf/*`):
 *   - Only PDF and image files are accepted.
 *   - Images are re-encoded as JPEG (quality 0.7 — max practical compression
 *     that keeps clinical text legible) with a white background, then placed
 *     on A4 pages at exactly 72 DPI (1 image pixel = 1 PDF point).
 *   - Images wider than an A4 page at 72 DPI are downsampled to page width;
 *     images taller than one page are tiled across multiple pages (no further
 *     detail loss). Small images are never upscaled.
 *   - PDFs pass through unchanged (assumed already reasonable).
 *
 * A4 @ 72 DPI: 595.28 × 841.89 pt (jsPDF unit "pt", format "a4").
 * Stream compression: jsPDF `compress: true` (Flate) on all object streams.
 */

import { jsPDF } from "jspdf";

export const A4_PT_WIDTH = 595.28;
export const A4_PT_HEIGHT = 841.89;

/** JPEG quality for re-encoded images — tuned for maximum compression with legible text. */
const JPEG_QUALITY = 0.7;

export interface ConvertedPdf {
  /** The PDF payload to upload (original file for PDFs, generated PDF for images). */
  blob: Blob;
  /** File name with a `.pdf` extension. */
  fileName: string;
  sizeBytes: number;
  sourceType: "pdf" | "image";
}

export function isPdfFile(file: File): boolean {
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name);
}

export function isImageFile(file: File): boolean {
  if (file.type.startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(file.name);
}

/** Convert one user-selected file to a PDF Blob per the upload policy above. */
export async function convertToPdf(file: File): Promise<ConvertedPdf> {
  if (isPdfFile(file)) {
    return { blob: file, fileName: pdfName(file.name), sizeBytes: file.size, sourceType: "pdf" };
  }
  if (isImageFile(file)) {
    const blob = await imageToPdfBlob(file);
    return { blob, fileName: pdfName(file.name), sizeBytes: blob.size, sourceType: "image" };
  }
  throw new Error("Only PDF and image files can be uploaded (images are converted to PDF).");
}

function pdfName(name: string): string {
  return name.replace(/\.[^.]+$/, "") + ".pdf";
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error(`Could not read image: ${file.name}`)); };
    img.src = url;
  });
}

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  return canvas;
}

/** Draw a source onto a white-backed canvas (JPEG has no alpha; avoid black fill). */
function drawWhiteBackground(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
}

async function imageToPdfBlob(file: File): Promise<Blob> {
  const img = await loadImage(file);

  // --- 72-DPI sizing: 1 image pixel = 1 PDF point ---
  // Never upscale. Downsample only when wider than an A4 page at 72 DPI;
  // extra height is handled by tiling across pages (preserves native density).
  let w = img.naturalWidth;
  let h = img.naturalHeight;
  if (w > A4_PT_WIDTH) {
    const s = A4_PT_WIDTH / w;
    w = Math.floor(w * s);
    h = Math.max(1, Math.floor(h * s));
  }
  if (w < 1) w = 1;

  // Render the full image once at final pixel density.
  const full = makeCanvas(w, h);
  const fctx = full.getContext("2d")!;
  drawWhiteBackground(fctx, w, h);
  fctx.drawImage(img, 0, 0, w, h);

  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  const pageH = Math.floor(A4_PT_HEIGHT);
  const pages = Math.max(1, Math.ceil(h / pageH));

  for (let i = 0; i < pages; i++) {
    const chunkH = Math.min(pageH, h - i * pageH);
    const page = makeCanvas(w, chunkH);
    const pctx = page.getContext("2d")!;
    drawWhiteBackground(pctx, w, chunkH);
    pctx.drawImage(full, 0, i * pageH, w, chunkH, 0, 0, w, chunkH);
    const jpeg = page.toDataURL("image/jpeg", JPEG_QUALITY);
    if (i > 0) doc.addPage();
    // px == pt ⇒ exactly 72 DPI, anchored top-left, full page width used.
    doc.addImage(jpeg, "JPEG", 0, 0, w, chunkH);
  }

  return doc.output("blob");
}
